import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, GripVertical, Inbox, UserRound, X, Copy, ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Chip = ({ p, team, onDragStart, onDragEnd, onDrop: onRemove, onSelect, selected, busy }) => (
  <div
    draggable
    onDragStart={onDragStart}
    onDragEnd={onDragEnd}
    onClick={onSelect}
    className={`group flex items-center gap-1.5 px-2 py-1.5 rounded cursor-grab active:cursor-grabbing border ${
      selected
        ? "bg-emerald-500/20 border-emerald-400 ring-1 ring-emerald-400"
        : p.on_multiple_squads
          ? "bg-amber-500/10 border-amber-500/50 hover:bg-amber-500/20"
          : "bg-slate-800/80 border-slate-700/60 hover:bg-slate-800"
    }`}
    data-testid={`drag-chip-${p.player_id}`}
    title={
      p.squads?.length
        ? `${p.name} · plays for ${p.squads.map((s) => `${s.name} (${s.age_group})`).join(", ")}`
        : `${p.name}${p.membership_number ? ` · ${p.membership_number}` : ""}`
    }
  >
    <GripVertical className="w-3 h-3 text-slate-600 shrink-0" />
    <span className="text-xs text-slate-200 truncate flex-1">{p.name}</span>
    {p.on_multiple_squads && (
      <span
        className="text-[10px] text-amber-300 shrink-0 font-semibold"
        data-testid={`multi-squad-${p.player_id}`}
      >
        {p.squad_count} teams
      </span>
    )}
    {p.played_up && !team.is_bucket && (
      <ArrowUpRight className="w-3 h-3 text-sky-400 shrink-0" title="Playing up" />
    )}
    {!team.is_bucket && (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        disabled={busy}
        className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-500 hover:text-red-400 shrink-0"
        data-testid={`drop-chip-${team.id}-${p.player_id}`}
        title={`Take ${p.name} off ${team.name}`}
      >
        <X className="w-3 h-3" />
      </button>
    )}
  </div>
);

export const SquadDragBoard = ({ ageGroup, onMoved }) => {
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dragging, setDragging] = useState(null);
  const [picked, setPicked] = useState(null); // { player, fromTeamId } — tap-to-place (iPad friendly)
  const [hoverTeam, setHoverTeam] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/academy/admin/academy-teams/age-group-board`, {
        params: { age_group: ageGroup },
        withCredentials: true,
      });
      setBoard(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load the squad board");
    } finally {
      setLoading(false);
    }
  }, [ageGroup]);

  useEffect(() => { load(); }, [load]);

  const drop = async (toTeam, override) => {
    const payload = override || dragging;
    setHoverTeam(null);
    setDragging(null);
    setPicked(null);
    if (!payload || toTeam.is_bucket || payload.fromTeamId === toTeam.id) return;
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/academy/admin/academy-teams/move-player`,
        { player_id: payload.player.player_id, from_team_id: payload.fromTeamId, to_team_id: toTeam.id },
        { withCredentials: true }
      );
      const extra = res.data.playing_up ? ` — playing up from ${res.data.true_age_group}` : "";
      toast.success(`${res.data.player_name} added to ${res.data.to_team_name}${extra}`, {
        description: res.data.squad_count > 1
          ? `Now on ${res.data.squad_count} squads. Nothing was taken away — use the x on a chip to remove.`
          : "Still in their age pool.",
      });
      await load();
      onMoved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add that player");
    } finally {
      setSaving(false);
    }
  };

  const removeFrom = async (team, player) => {
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/academy/admin/academy-teams/${team.id}/drop-player/${player.player_id}`,
        {},
        { withCredentials: true }
      );
      toast.success(`${res.data.player_name} taken off ${res.data.team_name}`);
      await load();
      onMoved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove that player");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-center" data-testid={`drag-board-loading-${ageGroup}`}>
        <Loader2 className="w-5 h-5 animate-spin mx-auto text-emerald-400" />
      </div>
    );
  }
  if (!board?.teams?.length) return null;

  const multiCount = new Set(
    [...board.teams, ...(board.play_up || [])]
      .flatMap((t) => t.players)
      .filter((p) => p.on_multiple_squads)
      .map((p) => p.player_id)
  ).size;

  const column = (t) => {
    const isTarget = hoverTeam === t.id && dragging && !t.is_bucket && dragging.fromTeamId !== t.id;
    return (
      <div
        key={t.id}
        onDragOver={(e) => { if (!t.is_bucket) { e.preventDefault(); setHoverTeam(t.id); } }}
        onDragLeave={() => setHoverTeam((h) => (h === t.id ? null : h))}
        onDrop={(e) => { e.preventDefault(); drop(t); }}
        className={`w-60 shrink-0 rounded-lg border p-3 transition-colors ${
          isTarget
            ? "border-emerald-400 bg-emerald-500/10"
            : t.is_bucket
              ? "border-slate-800 bg-slate-900/60"
              : t.is_play_up
                ? "border-sky-800/60 bg-sky-950/20"
                : "border-slate-700 bg-slate-900/40"
        }`}
        data-testid={`drag-column-${t.id}`}
      >
        <div className="flex items-start gap-2 mb-2">
          {t.is_bucket ? (
            <Inbox className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
          ) : t.is_play_up ? (
            <ArrowUpRight className="w-4 h-4 text-sky-400 mt-0.5 shrink-0" />
          ) : (
            <UserRound className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
          )}
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white truncate">{t.name}</p>
            <p className="text-[11px] text-slate-500">
              {t.players.length} {t.players.length === 1 ? "player" : "players"}
              {t.is_bucket ? " · age pool" : t.is_play_up ? ` · ${t.age_group} · playing up` : ""}
            </p>
          </div>
        </div>
        {picked && !t.is_bucket && picked.fromTeamId !== t.id && (
          <button
            type="button"
            onClick={() => drop(t, picked)}
            disabled={saving}
            className="w-full mb-2 px-2 py-1.5 text-[11px] rounded border border-dashed border-emerald-500/60 text-emerald-300 hover:bg-emerald-500/10"
            data-testid={`place-here-${t.id}`}
          >
            Add {picked.player.name} here
          </button>
        )}
        <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
          {t.players.length === 0 && (
            <p className="text-[11px] text-slate-600 italic py-3 text-center">
              {t.is_bucket ? "Pool is empty" : "Drop players here"}
            </p>
          )}
          {t.players.map((p) => (
            <Chip
              key={p.player_id}
              p={p}
              team={t}
              busy={saving}
              onDragStart={() => setDragging({ player: p, fromTeamId: t.id })}
              onDragEnd={() => { setDragging(null); setHoverTeam(null); }}
              onDrop={() => removeFrom(t, p)}
              selected={picked?.player.player_id === p.player_id && picked?.fromTeamId === t.id}
              onSelect={() => setPicked((cur) =>
                cur?.player.player_id === p.player_id && cur?.fromTeamId === t.id
                  ? null
                  : { player: p, fromTeamId: t.id })}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 bg-slate-950/60 border-t border-slate-800" data-testid={`drag-board-${ageGroup}`}>
      <div className="flex items-start gap-2 mb-3 flex-wrap">
        <Copy className="w-3.5 h-3.5 text-slate-500 mt-0.5 shrink-0" />
        <p className="text-xs text-slate-400 max-w-3xl">
          Dragging <strong className="text-slate-200">adds</strong> a squad — it never takes a player
          off the one they came from, so the same child can turn out for JSSL on Sunday and SYL on
          Saturday and still sit in their age pool. Anyone on more than one squad is{" "}
          <span className="text-amber-300">highlighted in amber</span>. On a tablet, tap a name then
          tap <em>Add … here</em> on the squad. To take someone off a squad, hover their name and
          click the ×.
          {saving && <Loader2 className="w-3 h-3 animate-spin inline ml-2 text-emerald-400" />}
        </p>
        {multiCount > 0 && (
          <span
            className="px-2 py-0.5 text-[11px] rounded bg-amber-500/15 text-amber-300 border border-amber-500/40 shrink-0"
            data-testid={`multi-squad-count-${ageGroup}`}
          >
            {multiCount} on more than one squad
          </span>
        )}
      </div>
      <div className="flex gap-3 overflow-x-auto pb-2">
        {board.teams.map(column)}
        {board.play_up?.length > 0 && (
          <>
            <div className="shrink-0 w-px bg-slate-800 mx-1" />
            {board.play_up.map(column)}
          </>
        )}
      </div>
      {board.play_up?.length > 0 && (
        <p className="text-[11px] text-sky-400/80 mt-2">
          The columns after the divider are older squads — drop a {board.age_group} player there to
          play them up. Dropping into a younger squad is blocked.
        </p>
      )}
    </div>
  );
};
